import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { FiUser, FiLock, FiLogIn, FiBox, FiTrendingUp, FiEye, FiEyeOff, FiLoader, FiArrowRight } from 'react-icons/fi';
import AuthContext from '../context/AuthContext';
import { toast } from 'react-toastify';
import logo from '../assets/logo.png';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showResendLink, setShowResendLink] = useState(false);
  const { login } = useContext(AuthContext);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!username || !password) {
      toast.error('Veuillez remplir tous les champs.');
      return;
    }
    try {
      await login({ username, password });
    } catch (err) {
      if (err.response && err.response.status === 403) {
        toast.error(err.response.data.message || "Veuillez vérifier votre email avant de vous connecter.");
        setShowResendLink(true);
      } else {
        toast.error('Nom d\'utilisateur ou mot de passe incorrect.');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Colonne de gauche (Branding) */}
      <div className="hidden lg:flex w-1/2 bg-brand-blue items-center justify-center p-12 text-white flex-col relative overflow-hidden font-poppins">
        <FiBox className="absolute -bottom-24 -right-24 text-white/5 w-96 h-96 transform rotate-12" />
        <FiTrendingUp className="absolute -top-20 -left-24 text-white/5 w-80 h-80 transform rotate-12" />
        <div className="relative z-10 text-center">
          <h1 className="text-4xl font-bold mb-4">Heureux de vous revoir !</h1>
          <p className="text-lg text-blue-100 max-w-md">
            La solution simple et efficace pour gérer votre boutique, où que vous soyez.
          </p>
          <div className="mt-10 border-t border-blue-400/30 pt-6">
            <p className="italic text-blue-200">"Depuis que j'utilise Jaapand-e, j'ai une vision claire de mon stock et de mes bénéfices."</p>
            <p className="mt-2 font-semibold">- Awa Ndiaye, Gérante</p>
          </div>
        </div>
      </div>

      {/* Colonne de droite (Formulaire) */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md space-y-8">
          <div className="text-left">
            <Link to="/">
              <img src={logo} alt="Jaapand-e" className="h-12 mb-6" />
            </Link>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-poppins tracking-tight">Bon retour ! 👋</h2>
            <p className="mt-2 text-gray-500 text-lg">Connectez-vous pour gérer votre boutique.</p>
          </div>

          <form onSubmit={handleLogin} className="mt-8 space-y-6">
            <div className="space-y-5">
              <div>
                <label htmlFor="username" className="block text-sm font-bold text-gray-700 mb-2 ml-1">Nom d'utilisateur</label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors">
                    <FiUser size={20} />
                  </div>
                  <input 
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-gray-50 border border-gray-100 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue focus:bg-white transition-all duration-200" 
                    id="username" type="text" placeholder="Votre nom d'utilisateur" 
                    value={username} onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2 ml-1">
                  <label htmlFor="password" class="block text-sm font-bold text-gray-700">Mot de passe</label>
                  <Link to="/forgot-password" class="text-sm font-medium text-brand-blue hover:text-blue-700 transition-colors">Mot de passe oublié ?</Link>
                </div>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-brand-blue transition-colors">
                    <FiLock size={20} />
                  </div>
                  <input 
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-gray-50 border border-gray-100 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue focus:bg-white transition-all duration-200" 
                    id="password" 
                    type={showPassword ? "text" : "password"} 
                    placeholder="••••••••" 
                    value={password} onChange={(e) => setPassword(e.target.value)}
                  />
                  <div className="absolute top-1/2 right-4 -translate-y-1/2 cursor-pointer" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <FiEyeOff className="text-gray-500" /> : <FiEye className="text-gray-500" />}
                  </div>
                </div>
              </div>
            </div>

            <button 
              className="w-full flex justify-center items-center py-4 px-4 border border-transparent rounded-xl shadow-lg shadow-brand-blue/20 text-base font-bold text-white bg-brand-blue hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue transition-all duration-200 transform hover:-translate-y-0.5 disabled:opacity-75 disabled:cursor-not-allowed" 
              type="submit"
              disabled={loading}
            >
              {loading ? <FiLoader className="animate-spin mr-2" /> : <FiArrowRight className="mr-2" />}
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>

            {showResendLink && (
              <div className="text-center mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                <p className="text-sm text-yellow-800 mb-2">Vous n'avez pas reçu l'email ?</p>
                <Link to="/resend-verification" className="text-sm font-bold text-brand-blue hover:underline">Renvoyer le lien de vérification</Link>
              </div>
            )}

            <p className="text-center text-sm text-gray-600 mt-8">
              Pas encore de compte ? <Link to="/register" className="font-bold text-brand-blue hover:text-blue-700 transition-colors">Créer un compte gratuitement</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;